// ============================================
// CAP.11 D#8 — Hallucination Prevention Monitoring Dashboard
//
// Real-time monitoring of 4 key metrics:
//   c1: Validation pass rate (target >95%)
//   c2: Human review queue depth (alert >10 cases)
//   c3: Average processing time (alert >5 minutes)
//   c4: Phantom value rate (alert >1% in 1-hour window)
//
// Data source: cap11GetMonitoringMetrics callable + Firestore snapshots
// ============================================

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { functions, db } from '../../../services/firebase';
import {
  Activity, AlertTriangle, Clock, Eye, Ghost, RefreshCw, CheckCircle2, ChevronRight, X,
} from 'lucide-react';

// ── Design Tokens ─────────────────────────────────────────────────────

const C = {
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  rose: '#E11D48',
  roseSoft: '#FFF1F2',
  pass: '#059669',
  passBg: '#D1FAE5',
  warn: '#B45309',
  warnBg: '#FEF3C7',
  fail: '#E11D48',
  failBg: '#FFF1F2',
  bg: '#FAFAFA',
  bgCard: '#FFFFFF',
  border: '#E4E4E7',
};

// ── Types ─────────────────────────────────────────────────────────────

interface Cap11MonitoringSnapshot {
  timestamp: string;
  validationPassRate: number;
  validationTotal: number;
  validationPassed: number;
  validationPassRateAlert: boolean;
  reviewQueueDepth: number;
  reviewQueueDepthAlert: boolean;
  averageProcessingTimeMs: number;
  processingTimeAlert: boolean;
  phantomValueRate: number;
  phantomEvaluationTotal: number;
  phantomDetectedCount: number;
  phantomRateAlert: boolean;
  collectionErrors: string[];
}

type DrillDownMetric = 'validation' | 'queue' | 'processing' | 'phantom';

interface DrillDownEntry {
  id: string;
  timestamp: string;
  type: string;
  status: string;
  summary: string;
  biomarker?: string;
  priority?: string;
}

// ── Thresholds ────────────────────────────────────────────────────────

const THRESHOLDS = {
  passRate: 0.95,
  queueDepth: 10,
  processingTimeMs: 5 * 60 * 1000,
  phantomRate: 0.01,
} as const;

// ── Metric Card ───────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  detail,
  alert,
  icon: Icon,
  threshold,
  onDrillDown,
}: {
  label: string;
  value: string;
  detail: string;
  alert: boolean;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  threshold: string;
  onDrillDown?: () => void;
}) {
  const statusColor = alert ? C.fail : C.pass;
  const statusBg = alert ? C.failBg : C.passBg;
  const statusLabel = alert ? 'ALERT' : 'OK';

  return (
    <div
      onClick={onDrillDown}
      style={{
        background: C.bgCard,
        border: `1px solid ${alert ? C.rose : C.border}`,
        borderRadius: 4,
        padding: 20,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        cursor: onDrillDown ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={18} style={{ color: statusColor }} />
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, fontWeight: 600, color: C.text }}>
            {label}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 13,
              fontWeight: 600,
              color: statusColor,
              background: statusBg,
              padding: '2px 8px',
              borderRadius: 4,
            }}
          >
            {statusLabel}
          </span>
          {onDrillDown && (
            <ChevronRight size={14} style={{ color: C.textMuted }} />
          )}
        </div>
      </div>

      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 28, fontWeight: 700, color: statusColor }}>
        {value}
      </div>

      <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.textSec }}>
        {detail}
      </div>

      <div
        style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 13,
          color: C.textMuted,
          borderTop: `1px solid ${C.border}`,
          paddingTop: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span>Threshold: {threshold}</span>
        {onDrillDown && (
          <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.rose }}>
            Investigate
          </span>
        )}
      </div>
    </div>
  );
}

// ── History Row ───────────────────────────────────────────────────────

function HistoryRow({ snapshot }: { snapshot: Cap11MonitoringSnapshot }) {
  const ts = new Date(snapshot.timestamp);
  const time = ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const hasAlert =
    snapshot.validationPassRateAlert ||
    snapshot.reviewQueueDepthAlert ||
    snapshot.processingTimeAlert ||
    snapshot.phantomRateAlert;

  return (
    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
      <td style={{ padding: '8px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: C.textSec }}>
        {time}
      </td>
      <td style={{ padding: '8px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: snapshot.validationPassRateAlert ? C.fail : C.pass }}>
        {(snapshot.validationPassRate * 100).toFixed(1)}%
      </td>
      <td style={{ padding: '8px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: snapshot.reviewQueueDepthAlert ? C.fail : C.text }}>
        {snapshot.reviewQueueDepth}
      </td>
      <td style={{ padding: '8px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: snapshot.processingTimeAlert ? C.fail : C.text }}>
        {(snapshot.averageProcessingTimeMs / 1000 / 60).toFixed(1)}m
      </td>
      <td style={{ padding: '8px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: snapshot.phantomRateAlert ? C.fail : C.text }}>
        {(snapshot.phantomValueRate * 100).toFixed(2)}%
      </td>
      <td style={{ padding: '8px 12px' }}>
        {hasAlert ? (
          <AlertTriangle size={14} style={{ color: C.fail }} />
        ) : (
          <CheckCircle2 size={14} style={{ color: C.pass }} />
        )}
      </td>
    </tr>
  );
}

// ── Drill-Down Panel ─────────────────────────────────────────────────

const METRIC_LABELS: Record<DrillDownMetric, string> = {
  validation: 'Validation Failures',
  queue: 'Pending Review Cases',
  processing: 'Extraction Processing',
  phantom: 'Phantom Detections',
};

function DrillDownPanel({
  metric,
  entries,
  loading: panelLoading,
  onClose,
}: {
  metric: DrillDownMetric;
  entries: DrillDownEntry[];
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        background: C.bgCard,
        border: `1px solid ${C.rose}`,
        borderRadius: 4,
        marginBottom: 24,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '12px 20px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: C.roseSoft,
        }}
      >
        <div>
          <h3
            style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 15,
              fontWeight: 600,
              color: C.text,
              margin: 0,
            }}
          >
            {METRIC_LABELS[metric]}
          </h3>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.textSec, margin: '2px 0 0' }}>
            Audit log entries from the last hour — click to investigate
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <X size={18} style={{ color: C.textSec }} />
        </button>
      </div>

      {panelLoading ? (
        <div style={{ padding: 24, fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.textSec }}>
          Loading audit entries...
        </div>
      ) : entries.length === 0 ? (
        <div style={{ padding: 24, fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.textSec }}>
          No entries found in the last hour for this metric.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Time', 'Status', 'Summary', 'ID'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '10px 12px',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: 13,
                      fontWeight: 600,
                      color: C.textMuted,
                      textAlign: 'left',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const ts = entry.timestamp ? new Date(entry.timestamp) : null;
                const time = ts ? ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
                const statusColor =
                  entry.status === 'failed' || entry.status === 'phantom' ? C.fail
                  : entry.status === 'pending' || entry.status === 'warning' ? C.warn
                  : C.pass;

                return (
                  <tr key={entry.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: C.textSec }}>
                      {time}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span
                        style={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontSize: 13,
                          fontWeight: 600,
                          color: statusColor,
                          background: entry.status === 'failed' || entry.status === 'phantom' ? C.failBg : entry.status === 'pending' || entry.status === 'warning' ? C.warnBg : C.passBg,
                          padding: '2px 8px',
                          borderRadius: 4,
                        }}
                      >
                        {entry.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.text }}>
                      {entry.summary}
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: C.textMuted }}>
                      {entry.id.length > 12 ? `${entry.id.slice(0, 12)}...` : entry.id}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────

export function Cap11MonitoringDashboard() {
  const [snapshot, setSnapshot] = useState<Cap11MonitoringSnapshot | null>(null);
  const [history, setHistory] = useState<Cap11MonitoringSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drillDown, setDrillDown] = useState<DrillDownMetric | null>(null);
  const [drillDownEntries, setDrillDownEntries] = useState<DrillDownEntry[]>([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  const fetchLiveMetrics = useCallback(async () => {
    try {
      const fn = httpsCallable<Record<string, never>, Cap11MonitoringSnapshot>(
        functions,
        'cap11GetMonitoringMetrics',
      );
      const result = await fn({});
      setSnapshot(result.data);
      setError(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[CAP11Dashboard] Failed to fetch live metrics:', msg);
      setError(msg);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const ref = collection(db, '_ops', 'cap11_monitoring', 'snapshots');
      const q = query(ref, orderBy('timestamp', 'desc'), limit(12));
      const snap = await getDocs(q);
      const items = snap.docs.map((d) => d.data() as Cap11MonitoringSnapshot);
      setHistory(items);
    } catch (err) {
      console.error('[CAP11Dashboard] Failed to fetch history:', err);
    }
  }, []);

  const fetchDrillDown = useCallback(async (metric: DrillDownMetric) => {
    setDrillDown(metric);
    setDrillDownLoading(true);
    setDrillDownEntries([]);
    try {
      const fn = httpsCallable<{ metric: string }, { metric: string; entries: DrillDownEntry[] }>(
        functions,
        'cap11GetDrillDownEntries',
      );
      const result = await fn({ metric });
      setDrillDownEntries(result.data.entries);
    } catch (err) {
      console.error('[CAP11Dashboard] Drill-down fetch failed:', err);
      setDrillDownEntries([]);
    } finally {
      setDrillDownLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchLiveMetrics(), fetchHistory()]);
      setLoading(false);
    })();
  }, [fetchLiveMetrics, fetchHistory]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchLiveMetrics(), fetchHistory()]);
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div style={{ padding: 32, fontFamily: 'DM Sans, sans-serif', color: C.textSec }}>
        Loading CAP.11 monitoring data...
      </div>
    );
  }

  return (
    <div style={{ padding: 24, background: C.bg, minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1
            style={{
              fontFamily: 'Instrument Serif, serif',
              fontSize: 24,
              fontWeight: 400,
              color: C.text,
              margin: 0,
            }}
          >
            Hallucination Prevention Monitor
          </h1>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.textMuted, margin: '4px 0 0' }}>
            CAP.11 D#8 — Real-time validation, queue, latency, and phantom tracking
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {snapshot && (
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.textMuted }}>
              Last: {new Date(snapshot.timestamp).toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              background: C.bgCard,
              border: `1px solid ${C.border}`,
              borderRadius: 4,
              cursor: refreshing ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 13,
              color: C.textSec,
              opacity: refreshing ? 0.6 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            <RefreshCw size={14} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            background: C.failBg,
            border: `1px solid ${C.rose}`,
            borderRadius: 4,
            padding: '12px 16px',
            marginBottom: 16,
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            color: C.fail,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <AlertTriangle size={16} />
          Failed to fetch live metrics: {error}
        </div>
      )}

      {/* Collection errors */}
      {snapshot && snapshot.collectionErrors.length > 0 && (
        <div
          style={{
            background: C.warnBg,
            border: `1px solid ${C.warn}`,
            borderRadius: 4,
            padding: '12px 16px',
            marginBottom: 16,
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 13,
            color: C.warn,
          }}
        >
          <strong>Collection warnings:</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {snapshot.collectionErrors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Metric cards */}
      {snapshot && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 16,
            marginBottom: 32,
          }}
        >
          {/* c1: Validation Pass Rate */}
          <MetricCard
            label="Validation Pass Rate"
            value={`${(snapshot.validationPassRate * 100).toFixed(1)}%`}
            detail={`${snapshot.validationPassed}/${snapshot.validationTotal} passed in last hour`}
            alert={snapshot.validationPassRateAlert}
            icon={CheckCircle2}
            threshold={`>${(THRESHOLDS.passRate * 100).toFixed(0)}%`}
            onDrillDown={() => fetchDrillDown('validation')}
          />

          {/* c2: Review Queue Depth */}
          <MetricCard
            label="Review Queue Depth"
            value={`${snapshot.reviewQueueDepth}`}
            detail={`${snapshot.reviewQueueDepth} case${snapshot.reviewQueueDepth !== 1 ? 's' : ''} pending review`}
            alert={snapshot.reviewQueueDepthAlert}
            icon={Eye}
            threshold={`Alert >${THRESHOLDS.queueDepth} cases`}
            onDrillDown={() => fetchDrillDown('queue')}
          />

          {/* c3: Avg Processing Time */}
          <MetricCard
            label="Avg Processing Time"
            value={`${(snapshot.averageProcessingTimeMs / 1000 / 60).toFixed(1)}m`}
            detail={`Average extraction time in last hour`}
            alert={snapshot.processingTimeAlert}
            icon={Clock}
            threshold={`Alert >5 minutes`}
            onDrillDown={() => fetchDrillDown('processing')}
          />

          {/* c4: Phantom Value Rate */}
          <MetricCard
            label="Phantom Value Rate"
            value={`${(snapshot.phantomValueRate * 100).toFixed(2)}%`}
            detail={`${snapshot.phantomDetectedCount}/${snapshot.phantomEvaluationTotal} phantoms in last hour`}
            alert={snapshot.phantomRateAlert}
            icon={Ghost}
            threshold={`Alert >1% in 1-hour window`}
            onDrillDown={() => fetchDrillDown('phantom')}
          />
        </div>
      )}

      {/* Drill-down panel */}
      {drillDown && (
        <DrillDownPanel
          metric={drillDown}
          entries={drillDownEntries}
          loading={drillDownLoading}
          onClose={() => setDrillDown(null)}
        />
      )}

      {/* No data state */}
      {!snapshot && !error && (
        <div
          style={{
            background: C.bgCard,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            padding: 32,
            textAlign: 'center',
            marginBottom: 32,
          }}
        >
          <Activity size={32} style={{ color: C.textMuted, marginBottom: 8 }} />
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: C.textSec, margin: 0 }}>
            No monitoring data available yet. Metrics are collected every 5 minutes.
          </p>
        </div>
      )}

      {/* History table */}
      {history.length > 0 && (
        <div
          style={{
            background: C.bgCard,
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
            <h2
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 15,
                fontWeight: 600,
                color: C.text,
                margin: 0,
              }}
            >
              Recent Snapshots
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: C.textMuted, margin: '2px 0 0' }}>
              Last {history.length} monitoring captures (5-minute intervals)
            </p>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  {['Time', 'Pass Rate', 'Queue', 'Avg Time', 'Phantom %', 'Status'].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: '10px 12px',
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: 13,
                        fontWeight: 600,
                        color: C.textMuted,
                        textAlign: 'left',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((snap, i) => (
                  <HistoryRow key={i} snapshot={snap} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
